import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import { Badge, Box } from '@mui/material';

import TPTooltip from './TPTooltip';
import UnsavedIndicator from './components/unsavedIndicator';
import { TreatmentPlanPages } from './constants/general';
import { FORTA_ERROR, FORTA_BLUE, FORTA_WHITE } from '../../../utils/colors';
import ClientInfoIcon from '../../../assets/images/client-info.svg';
import MedInfoIcon from '../../../assets/images/med-info.svg';
import MedicationsIcon from '../../../assets/images/medications.svg';
import TherapyHistoryIcon from '../../../assets/images/therapy-history.svg';
import BackgroundIcon from '../../../assets/images/background.svg';
import AssessmentIcon from '../../../assets/images/assessment.svg';
import CrisisPlanIcon from '../../../assets/images/crisis-plan.svg';
import GoalsIcon from '../../../assets/images/goals.svg';
import DischargeIcon from '../../../assets/images/discharge.svg';
import TreatmentIcon from '../../../assets/images/treatment.svg';

import type { RootState } from '../../../store';

interface NavItemProp {
  to: string;
  icon: {
    url: string;
    width: number;
    height: number;
  };
  badge: number;
  testId?: string;
  unsavedCount?: number;
}

const itemStyle = {
  display: 'flex',
  boxSizing: 'border-box',
  flexDirection: 'column',
  width: 60,
  height: 60,
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
  backgroundColor: FORTA_BLUE.primary,
  margin: '0 0.5em',
  border: '2px solid',
  position: 'relative',
  borderColor: FORTA_BLUE.primary,

  '&:hover': {
    borderColor: FORTA_WHITE,
  },
};

const activeStyle = {
  backgroundColor: FORTA_BLUE.light1,
  boxShadow: 'inset 0px 4px 4px rgba(0, 0, 0, 0.25)',
};

const NavItem = (props: NavItemProp) => {
  const { to, icon, badge = 0, testId, unsavedCount = 0 } = props;

  return (
    <Box
      data-testid={testId}
      sx={{
        borderRight: '2px solid',
        borderColor: FORTA_BLUE.light1,
        '&:last-child': {
          borderRight: 'none',
        },
      }}
    >
      <NavLink
        to={to}
        style={{
          textDecoration: 'none',
        }}
      >
        {({ isActive }) => (
          <>
            <Box
              sx={
                isActive
                  ? {
                      ...itemStyle,
                      ...activeStyle,
                    }
                  : {
                      ...itemStyle,
                    }
              }
            >
              <Badge
                badgeContent={badge}
                overlap="circular"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: FORTA_ERROR,
                    color: FORTA_WHITE,
                    fontWeight: 'bold',
                  },
                }}
              >
                <img src={icon.url} alt="nav-icon" width={icon.width} height={icon.height} />
              </Badge>
              {unsavedCount > 0 && (
                <UnsavedIndicator
                  title={`${unsavedCount}`}
                  fontSize={12}
                  top="undefined"
                  right="5px"
                  bottom="-3px"
                  sx={{ borderRadius: '50%', width: 24, height: 24, p: 0 }}
                />
              )}
            </Box>
          </>
        )}
      </NavLink>
    </Box>
  );
};

function TooltipContent() {
  return (
    <Box sx={{ display: 'flex' }}>
      {Object.entries(TreatmentPlanPages).map(([, label], i) => (
        <Box
          key={i}
          sx={{
            fontSize: '1em',
            height: '25px',
            width: '76px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRight: '2px solid',
            borderColor: FORTA_BLUE.primary,
            '&:last-child': {
              borderRight: 'none',
            },
          }}
        >
          {label}
        </Box>
      ))}
    </Box>
  );
}

function NavBar() {
  const { badges } = useSelector((state: any) => state.treatmentPlans);
  const { behaviorReductionGoals, skillAcquisitionGoals, caregiverGoals } = useSelector(
    (state: RootState) => state.goals,
  );

  const unsavedSAGCount = useMemo(() => {
    return (
      skillAcquisitionGoals?.reduce((acc, goal) => {
        let count = 0;
        if (goal?.dirty && goal?.is_editing) {
          count++;
        }
        goal?.targets?.forEach((target) => {
          if (target.dirty && target.is_editing) {
            count++;
          }
        });
        return acc + count;
      }, 0) ?? 0
    );
  }, [skillAcquisitionGoals]);

  const unsavedBRGCount = useMemo(() => {
    return (
      behaviorReductionGoals?.reduce((acc, goal) => {
        let count = 0;
        if (goal?.dirty && goal?.is_editing) {
          count++;
        }
        goal?.targets?.forEach((target) => {
          if (target.dirty && target.is_editing) {
            count++;
          }
        });
        return acc + count;
      }, 0) ?? 0
    );
  }, [behaviorReductionGoals]);

  const unsavedPCGCount = useMemo(() => {
    return (
      caregiverGoals?.reduce((acc, goal) => {
        let count = 0;
        if (goal?.dirty && goal?.is_editing) {
          count++;
        }
        goal?.targets?.forEach((target) => {
          if (target.dirty && target.is_editing) {
            count++;
          }
        });
        return acc + count;
      }, 0) ?? 0
    );
  }, [caregiverGoals]);

  const { treatmentPlanId } = useParams<'treatmentPlanId'>();

  return (
    <Box sx={{ display: 'flex', mt: 3, justifyContent: 'center' }}>
      <TPTooltip title={<TooltipContent />}>
        <Box
          sx={{
            backgroundColor: FORTA_BLUE.primary,
            borderRadius: '10px',
            display: 'flex',
            height: 80,
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
            alignItems: 'center',
            padding: '0 1em',
          }}
        >
          <NavItem
            testId="clientInfoNavButton"
            to={`/treatment-plan/${treatmentPlanId ? `${treatmentPlanId}/` : ''}client-info`}
            icon={{ url: ClientInfoIcon, width: 32, height: 40 }}
            badge={badges[TreatmentPlanPages.ClientInfo]}
          />
          <NavItem
            testId="medInfoNavButton"
            to={`/treatment-plan/${treatmentPlanId ? `${treatmentPlanId}/` : ''}med-info`}
            icon={{ url: MedInfoIcon, width: 32, height: 40 }}
            badge={badges[TreatmentPlanPages.MedInfo]}
          />
          <NavItem
            testId="medicationsNavButton"
            to={`/treatment-plan/${treatmentPlanId ? `${treatmentPlanId}/` : ''}medications`}
            icon={{ url: MedicationsIcon, width: 36, height: 39.41 }}
            badge={badges[TreatmentPlanPages.Medications]}
          />
          <NavItem
            testId="historyNavButton"
            to={`/treatment-plan/${treatmentPlanId ? `${treatmentPlanId}/` : ''}history`}
            icon={{ url: TherapyHistoryIcon, width: 38.94, height: 37 }}
            badge={badges[TreatmentPlanPages.History]}
          />
          <NavItem
            testId="backgroundNavButton"
            to={`/treatment-plan/${treatmentPlanId ? `${treatmentPlanId}/` : ''}background`}
            icon={{ url: BackgroundIcon, width: 42.35, height: 35.15 }}
            badge={badges[TreatmentPlanPages.Background]}
          />
          <NavItem
            testId="assessmentNavButton"
            to={`/treatment-plan/${treatmentPlanId ? `${treatmentPlanId}/` : ''}assessment`}
            icon={{ url: AssessmentIcon, width: 36, height: 36 }}
            badge={badges[TreatmentPlanPages.Assessment]}
          />
          <NavItem
            testId="crisisPlanNavButton"
            to={`/treatment-plan/${treatmentPlanId ? `${treatmentPlanId}/` : ''}crisis-plan`}
            icon={{ url: CrisisPlanIcon, width: 40, height: 43.5 }}
            badge={badges[TreatmentPlanPages.CrisisPlan]}
          />
          <NavItem
            testId="goalsNavButton"
            to={`/treatment-plan/${treatmentPlanId ? `${treatmentPlanId}/` : ''}goals`}
            icon={{ url: GoalsIcon, width: 33.95, height: 36 }}
            badge={badges[TreatmentPlanPages.Goals]}
            unsavedCount={unsavedBRGCount + unsavedSAGCount + unsavedPCGCount}
          />
          <NavItem
            testId="dischargeTransitionNavButton"
            to={`/treatment-plan/${treatmentPlanId ? `${treatmentPlanId}/` : ''}discharge-transition`}
            icon={{ url: DischargeIcon, width: 36, height: 36.1 }}
            badge={badges[TreatmentPlanPages.DischargeTransition]}
          />
          <NavItem
            testId="treatmentRecommendationsNavButton"
            to={`/treatment-plan/${treatmentPlanId ? `${treatmentPlanId}/` : ''}treatment-recommendation`}
            icon={{ url: TreatmentIcon, width: 40.65, height: 41.9 }}
            badge={badges[TreatmentPlanPages.TreatmentRecommendation]}
          />
        </Box>
      </TPTooltip>
    </Box>
  );
}

export default NavBar;
